/**
 * ops 依頼の一覧（Queue / システム運用タブ共通）。対象範囲（scope）だけが違い、ステータスタブ・サービス
 * 絞り込み・検索・並び替え・スクロール読み込みは同じものを使う（タブごとに一覧を複製しない）。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops 依頼を「ID / 起票 / 緊急度 / SLO / 対応者 / 進捗 / 依頼」の表で一覧する共通部品。対象範囲 scope（requests=依頼者のいる依頼、system=システムオペレーション）を一覧取得とステータス件数の両方に渡す。ステータス（All / Open / Investigating / Blocked / Closed）とサービスで絞り込み、検索ボックスで番号（#412 等）またはフリーワードをサーバ側で絞り込む（入力は IME 確定後にデバウンスして送る）。履歴含む全件を offset ページングでスクロール読み込みする（全件はロードしない）。ID / 起票 / 緊急度 / SLO の列見出しをクリックすると取得済みの行を昇順・降順で並び替える（絞り込みは維持し、未取得ページが残る間はその旨を注記する）。SLO 見出しの隣の ? から目標営業日の説明を開ける。システムオペレーションは SLO を持たないので system では SLO 列を出さない。ローディングはスケルトン、0 件は空表示（検索中は検索語つき）、失敗は再試行付きエラー表示にする
 * @graph-connects useOpsRequestsPage [calls] 依頼のページ取得（GET /ops/requests、scope 付き）
 * @graph-connects flattenOpsRequestPages [calls] 取得済みページを id 一意の行リストへ畳む
 * @graph-connects useOpsStatusCounts [calls] ステータス別件数の集計取得（GET /ops/requests/status-counts、scope 付き）
 * @graph-connects sortOpsQueueRows [calls] 取得済みの行を選択中の列 / 方向で並び替え
 * @graph-connects useDebouncedValue [calls] 検索語の反映を遅らせて打鍵ごとの再取得を防ぐ
 * @graph-connects deriveOpsSearchTokens [calls] 検索語が実質空（空白のみ）かの判定
 * @graph-connects OpsSloExplainer [references] SLO 列見出しの隣の ? から目標営業日の説明を開く
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { Select, type SelectOption } from '@cortex/web-design-system/components/ui/Select';
import { Skeleton } from '@cortex/web-design-system/components/ui/Skeleton';
import { TabButton } from '../../ui/Tabs/TabButton.js';
import { TabGroup } from '../../ui/Tabs/TabGroup.js';
import { flattenOpsRequestPages } from '../../../lib/ops-request-pages.js';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue.js';
import {
  useOpsRequestsPage,
  useOpsStatusCounts,
  type OpsServiceFilter,
  type OpsStatusFilter,
} from '../../../hooks/useOps.js';
import { sortOpsQueueRows, type OpsQueueSortKey } from '../../../lib/ops-queue-sort.js';
import { OPS_SERVICE_LABELS } from '../../../lib/ops-fixtures.js';
import { SlaChip, StateBadge, UrgencyBadge } from './OpsPrimitives.js';
import { SortableHeader, type SortDir } from '../KpiDetailModal/SortableHeader.js';
import { OpsSloExplainer } from './OpsSloExplainer.js';
import type { OpsService } from '../../../types/ops.js';
import {
  deriveOpsSearchTokens,
  OPS_SERVICE_SLUGS,
  opsDisplayNo,
  computeOpsSla,
  jstDateKey,
  type OpsListScope,
} from '@cortex/ops-core';

/** 絞り込みタブ定義（All + 各ステータス。Closed = 終了 / それ以外 = 非終了）。 @graph-connects none */
const STATUS_TABS: OpsStatusFilter[] = ['all', 'open', 'investigating', 'blocked', 'closed'];

/** 絞り込み用サービス並び（ops-core を単一 source とし、サービス追加に自動追従する）。 @graph-connects none */
const SERVICES: readonly OpsService[] = OPS_SERVICE_SLUGS;

/** ローディング時に見せるスケルトン行のキー。 @graph-connects none */
const SKELETON_KEYS = [0, 1, 2, 3, 4, 5];

/** テーブルの列数（空 / エラー / ローディング行の colSpan）。SLO 列を出さない範囲では 1 つ減る。 @graph-connects none */
const QUEUE_COLUMNS = 7;

/**
 * 起票日時を JST の M/D 表記にする（toLocaleString は使わない）。閲覧者の端末 TZ で日付を取ると、JST 0〜2 時の
 * 起票がベトナム（UTC+7）では前日に見えるため、SLA と同じ JST 暦日で出す。読めない値は「—」。
 * @graph-connects jstDateKey [calls] JST 暦日（YYYY-MM-DD）
 */
function formatMonthDay(iso: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '—';
  const [, month = '', day = ''] = jstDateKey(new Date(ms)).split('-');
  return `${String(Number(month))}/${String(Number(day))}`;
}

/** 検索ボックスの虫眼鏡アイコン。絞り込み中はアクセント色にして状態を色でも示す。 @graph-connects none */
function SearchIcon({ active }: { active: boolean }): ReactNode {
  return (
    <svg
      className={`h-4 w-4 ${active ? 'text-[var(--color-accent-primary)]' : ''}`}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      aria-hidden="true"
    >
      <circle cx="7" cy="7" r="4.5" />
      <path d="M10.5 10.5L14 14" strokeLinecap="round" />
    </svg>
  );
}

/** 列の aria-sort 値（アクティブ列だけが方向を持つ）。 @graph-connects none */
function ariaSortFor(
  key: OpsQueueSortKey,
  activeKey: OpsQueueSortKey | null,
  dir: SortDir,
): 'ascending' | 'descending' | 'none' {
  if (activeKey !== key) return 'none';
  return dir === 'asc' ? 'ascending' : 'descending';
}

/** OpsRequestQueue の props。 */
export interface OpsRequestQueueProps {
  /** 対象範囲（requests = Queue / system = システム運用タブ）。一覧と件数の両方に同じ値を渡す。 */
  scope: OpsListScope;
}

/** ops 依頼の一覧を描画する（Queue / システム運用タブ共通）。 @graph-connects none */
export function OpsRequestQueue({ scope }: OpsRequestQueueProps): ReactNode {
  const { t } = useTranslation();
  // システムオペレーションは SLO を持たない（ops-core の目標営業日が null）。全行が「—」の列は出さない。
  const showSlo = scope === 'requests';
  const columns = showSlo ? QUEUE_COLUMNS : QUEUE_COLUMNS - 1;
  const [status, setStatus] = useState<OpsStatusFilter>('all');
  const [service, setService] = useState<OpsServiceFilter>('all');
  const [sortKey, setSortKey] = useState<OpsQueueSortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  // 入力は即時に表示し（searchInput）、通信のトリガーになる値（searchTerm）だけ遅らせる。
  // searchTerm は **IME 変換が確定してから**しか更新しない。React の onChange は DOM の input
  // イベント由来で変換中にも発火するため、変換候補を選ぶ数百 ms の間に未確定の読み（"へんぴん"）で
  // 本当にクエリが飛び、無駄な BQ スキャンと一瞬の 0 件表示が起きる（日本語が主のキューでは常道の操作）。
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const composingRef = useRef(false);
  // 前後の空白だけ違う入力を別のクエリキーにしない（"返品 " と "返品" で 2 回取りに行かない）。
  const q = useDebouncedValue(searchTerm).trim();
  // 実際に絞り込みが掛かっているか（空白のみの入力は絞り込まないので「検索中」ではない）。
  const isSearching = deriveOpsSearchTokens(q).length > 0;
  const sentinelRef = useRef<HTMLDivElement>(null);

  const query = useOpsRequestsPage(status, service, { q, scope });
  // バッジも一覧と同じ条件で数える（検索中にバッジだけ全件だと数が食い違う）。
  const counts = useOpsStatusCounts(service, q, scope).data;
  // 取得済みページを id 一意へ畳んでから並び替える（重複を落としてから並べないと、同じ依頼が
  // 離れた位置に 2 行出る）。並び替えの対象は取得済みの行だけで、絞り込みは query key が持つため
  // ソートで status / service が失われることはない。次ページが来るたび結合後の全行に再適用される。
  const rows = useMemo(
    () => sortOpsQueueRows(flattenOpsRequestPages(query.data), sortKey, sortDir),
    [query.data, sortKey, sortDir],
  );
  const total = query.data?.pages[0]?.total ?? 0;
  const { hasNextPage, isFetchingNextPage, fetchNextPage, isLoading, isError, refetch } = query;

  /** 同じ列なら方向を反転し、別の列なら昇順で切り替える（他テーブルと同じ 2 状態トグル）。 */
  const handleSort = (key: OpsQueueSortKey): void => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir('asc');
  };

  // スクロールで sentinel が見えたら次ページを取得（全件をロードしない）
  useEffect(() => {
    const el = sentinelRef.current;
    if (el === null) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting) && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage();
      }
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const serviceOptions: SelectOption[] = [
    { value: 'all', label: t('ops.common.allServices') },
    ...SERVICES.map((s) => ({ value: s, label: OPS_SERVICE_LABELS[s] })),
  ];

  return (
    <Card padding="none">
      <div className="space-y-2 border-b border-[var(--color-border)] px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <TabGroup ariaLabel={t('ops.common.statusFilterLabel')}>
            {STATUS_TABS.map((tab) => (
              <TabButton
                key={tab}
                active={status === tab}
                label={t(`ops.common.statusFilter.${tab}`)}
                count={counts?.[tab] ?? 0}
                onClick={() => {
                  setStatus(tab);
                }}
              />
            ))}
          </TabGroup>
          <div className="w-full sm:w-56">
            <Select
              aria-label={t('ops.common.serviceFilterLabel')}
              options={serviceOptions}
              value={service}
              onChange={(e) => {
                setService(e.target.value as OpsServiceFilter);
              }}
              fullWidth
              size="sm"
            />
          </div>
        </div>
        {/* 検索は常に独立した行に置く。タブと同じ行へ並べると、検索でバッジ件数が 4 桁（8261）から
            1〜2 桁（23）へ縮んだ瞬間に TabGroup が細くなり、収まるようになった検索ボックスが
            上の行へ飛び上がる。行を分けてタブ幅への依存を断つ。 */}
        <div className="w-full sm:w-80">
          <Input
            type="search"
            size="sm"
            value={searchInput}
            placeholder={t('ops.queue.searchPlaceholder')}
            aria-label={t('ops.queue.searchLabel')}
            leftIcon={<SearchIcon active={isSearching} />}
            // 既定では一段持ち上げた背景で「入力できる場所」だと分かるようにし、絞り込み中は
            // アクセント色の枠 + リングで「今フィルタが掛かっている」ことを色でも示す
            // （枠線だけだと暗い背景に埋もれて、絞り込み中かどうかが見分けにくい）。
            className={
              isSearching
                ? 'bg-[var(--color-bg-elevated)] border-[var(--color-accent-primary)] ring-1 ring-[var(--color-accent-primary)]'
                : 'bg-[var(--color-bg-elevated)]'
            }
            onChange={(e) => {
              setSearchInput(e.target.value);
              // 変換中は確定値を進めない（確定は onCompositionEnd 側で行う）。
              if (!composingRef.current) setSearchTerm(e.target.value);
            }}
            onCompositionStart={() => {
              composingRef.current = true;
            }}
            onCompositionEnd={(e) => {
              composingRef.current = false;
              setSearchTerm(e.currentTarget.value);
            }}
          />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="text-left text-xs text-[var(--color-text-muted)]">
              <th className="px-4 py-2 font-medium" aria-sort={ariaSortFor('id', sortKey, sortDir)}>
                <SortableHeader
                  label={t('ops.queue.columns.id')}
                  sortKey="id"
                  currentKey={sortKey}
                  currentDir={sortDir}
                  onSort={handleSort}
                />
              </th>
              <th
                className="px-2 py-2 font-medium"
                aria-sort={ariaSortFor('submitted', sortKey, sortDir)}
              >
                <SortableHeader
                  label={t('ops.queue.columns.submitted')}
                  sortKey="submitted"
                  currentKey={sortKey}
                  currentDir={sortDir}
                  onSort={handleSort}
                />
              </th>
              <th
                className="px-2 py-2 font-medium"
                aria-sort={ariaSortFor('urgency', sortKey, sortDir)}
              >
                <SortableHeader
                  label={t('ops.queue.columns.urgency')}
                  sortKey="urgency"
                  currentKey={sortKey}
                  currentDir={sortDir}
                  onSort={handleSort}
                />
              </th>
              {showSlo && (
                <th
                  className="px-2 py-2 font-medium"
                  aria-sort={ariaSortFor('slo', sortKey, sortDir)}
                  // 見出しセルの読み上げ名を列名だけに固定する。セル内の ? ボタンの aria-label まで
                  // 名前に含まれると、表を読み上げるたびに全行で「SLO、SLO の目安を表示」と繰り返される。
                  aria-label={t('ops.queue.columns.slo')}
                >
                  <span className="inline-flex items-center gap-1">
                    <SortableHeader
                      label={t('ops.queue.columns.slo')}
                      sortKey="slo"
                      currentKey={sortKey}
                      currentDir={sortDir}
                      onSort={handleSort}
                    />
                    <OpsSloExplainer />
                  </span>
                </th>
              )}
              <th className="min-w-[5rem] px-2 py-2 font-medium">
                {t('ops.queue.columns.assignee')}
              </th>
              <th className="px-2 py-2 font-medium">{t('ops.queue.columns.state')}</th>
              <th className="px-2 py-2 font-medium">{t('ops.queue.columns.request')}</th>
            </tr>
          </thead>
          <tbody>
            {isError && (
              <tr>
                <td colSpan={columns} className="px-4 py-10 text-center">
                  <div className="text-sm text-[var(--color-error)]">
                    {t('ops.common.fetchError')}
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="mt-3"
                    onClick={() => {
                      void refetch();
                    }}
                  >
                    {t('ops.common.retry')}
                  </Button>
                </td>
              </tr>
            )}
            {!isError &&
              isLoading &&
              SKELETON_KEYS.map((k) => (
                <tr key={`sk-${String(k)}`} className="border-t border-[var(--color-border)]">
                  <td className="px-4 py-2">
                    <Skeleton width={56} />
                  </td>
                  <td className="px-2 py-2">
                    <Skeleton width={40} />
                  </td>
                  <td className="px-2 py-2">
                    <Skeleton width={52} />
                  </td>
                  {showSlo && (
                    <td className="px-2 py-2">
                      <Skeleton width={52} />
                    </td>
                  )}
                  <td className="min-w-[5rem] px-2 py-2">
                    <Skeleton width={64} />
                  </td>
                  <td className="px-2 py-2">
                    <Skeleton width={72} />
                  </td>
                  <td className="px-2 py-2">
                    <Skeleton width="85%" />
                  </td>
                </tr>
              ))}
            {!isError && !isLoading && rows.length === 0 && (
              <tr>
                <td
                  colSpan={columns}
                  className="px-4 py-10 text-center text-sm text-[var(--color-text-muted)]"
                >
                  {deriveOpsSearchTokens(q).length === 0
                    ? t('ops.queue.empty')
                    : t('ops.queue.searchEmpty', { query: q })}
                </td>
              </tr>
            )}
            {!isError &&
              !isLoading &&
              rows.map((r) => (
                <tr
                  key={r.id}
                  className="border-t border-[var(--color-border)] transition-colors hover:bg-[var(--color-bg-hover)]"
                >
                  <td className="px-4 py-2 align-middle">
                    <Link
                      to="/ops/requests/$id"
                      params={{ id: r.id }}
                      className="font-semibold text-[var(--color-accent-primary)] hover:underline"
                    >
                      #{opsDisplayNo(r.id)}
                    </Link>
                  </td>
                  <td className="whitespace-nowrap px-2 py-2 text-[var(--color-text-muted)]">
                    {formatMonthDay(r.submittedAt)}
                  </td>
                  <td className="whitespace-nowrap px-2 py-2">
                    <UrgencyBadge urgency={r.urgency} />
                  </td>
                  {showSlo && (
                    <td className="whitespace-nowrap px-2 py-2">
                      <SlaChip sla={computeOpsSla(r)} />
                    </td>
                  )}
                  <td className="min-w-[5rem] whitespace-nowrap px-2 py-2 text-[var(--color-text-secondary)]">
                    {r.assigneeName ?? '—'}
                  </td>
                  <td className="px-2 py-2">
                    <StateBadge state={r.state} />
                  </td>
                  <td className="w-full max-w-0 px-2 py-2">
                    <div className="line-clamp-2 leading-snug text-[var(--color-text-primary)]">
                      {r.title}
                    </div>
                    <div className="mt-0.5 truncate text-xs text-[var(--color-text-muted)]">
                      {OPS_SERVICE_LABELS[r.service]} · {r.requesterName}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <div
        ref={sentinelRef}
        // 検索結果は入力停止から数百 ms 後に差し替わる。live region が無いと読み上げ利用者に
        // 表の中身が変わったことが伝わらない（WCAG 4.1.3）。
        aria-live="polite"
        className="flex items-center justify-center gap-3 border-t border-[var(--color-border)] px-4 py-3 text-xs text-[var(--color-text-muted)]"
      >
        {!isLoading && !isError && (
          <span
            className={isSearching ? 'font-semibold text-[var(--color-accent-primary)]' : undefined}
          >
            {t('ops.queue.countSummary', { shown: rows.length, total })}
          </span>
        )}
        {/* 並び替えは取得済みの行だけに効く。未取得ページが残る間は、全件を並べ替えた結果に見えないよう明示する。 */}
        {!isLoading && !isError && sortKey !== null && hasNextPage && (
          <span>{t('ops.queue.sortScopeNote')}</span>
        )}
        {isFetchingNextPage && <span>{t('ops.common.loading')}</span>}
        {hasNextPage && !isFetchingNextPage && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              void fetchNextPage();
            }}
          >
            {t('ops.common.loadMore')}
          </Button>
        )}
      </div>
    </Card>
  );
}
